import type { ComponentProps } from 'react'

import { cn } from '@/lib/utils'

type ChecklistState = 'complete' | 'incomplete' | 'info'

interface ChecklistItemProps extends ComponentProps<'div'> {
    state?: ChecklistState
}

const indicatorClasses: Record<ChecklistState, string> = {
    complete: 'bg-status-success-background text-status-success-foreground',
    incomplete: 'bg-status-error-background text-status-error-foreground',
    info: 'text-status-info-foreground'
}

/*
 * GoodUnited / ChecklistItem. Figma: 10 / Components / ChecklistItem.
 * A 20px status mark beside Roboto / text-base copy. complete and incomplete sit on a status tint, info has no tint.
 */
function ChecklistItem({ state = 'complete', className, children, ...props }: ChecklistItemProps) {
    return (
        <div
            data-slot='checklist-item'
            data-state={state}
            className={cn('flex items-center gap-2 py-1 text-base text-text-primary', className)}
            {...props}
        >
            <span className={cn('inline-flex size-5 shrink-0 items-center justify-center rounded-sm', indicatorClasses[state])} aria-hidden='true'>
                {state === 'incomplete'
                    ? (
                        <svg width='8' height='8' viewBox='0 0 8 8' fill='none'>
                            <path d='M1 1L7 7M7 1L1 7' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
                        </svg>
                    )
                    : (
                        <svg width='10' height='7' viewBox='0 0 10 7' fill='none'>
                            <path d='M1 3.5L3.75 6L9 1' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
                        </svg>
                    )}
            </span>
            <span className='sr-only'>
                {state === 'complete' ? 'Complete: ' : state === 'incomplete' ? 'Not complete: ' : 'Information: '}
            </span>
            <span>
                {children}
            </span>
        </div>
    )
}

export { ChecklistItem }
export type { ChecklistState }
